import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail } from "lucide-react";
import { SITE, LEGAL } from "@/lib/site-data";
import { openCookieSettings } from "@/lib/consent";

export const Footer = () => {
    return (
        <footer className="bg-brand-ink text-brand-cream" data-testid="site-footer">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-16 grid gap-12 lg:grid-cols-[1.2fr_1fr_1fr_1fr]">
                <div>
                    <img
                        src="/brand/logo_negative-400.webp"
                        alt="Pharmacie La Lutrive"
                        className="h-24 w-auto mb-6"
                        data-testid="footer-logo"
                    />
                    <p className="text-brand-cream/75 leading-relaxed max-w-sm font-sans">
                        Au cœur de Lutry, La Lutrive est une pharmacie indépendante où nous prenons le temps
                        de vous écouter, de vous conseiller et de vous accompagner.
                    </p>
                </div>

                <div>
                    <h4 className="ll-eyebrow text-brand-mint mb-5">Contact</h4>
                    <ul className="space-y-3 text-brand-cream/85 font-sans">
                        <li className="flex items-start gap-3">
                            <MapPin className="h-4 w-4 mt-1 text-brand-mint shrink-0" />
                            <span>
                                {SITE.address.line1}
                                <br />
                                {SITE.address.zip} {SITE.address.city}
                            </span>
                        </li>
                        <li className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-brand-mint" />
                            <a href={`tel:${SITE.phone}`} className="hover:text-brand-mint">
                                {SITE.phoneDisplay}
                            </a>
                        </li>
                        <li className="flex items-center gap-3">
                            <Mail className="h-4 w-4 text-brand-mint" />
                            <a href={`mailto:${SITE.email}`} className="hover:text-brand-mint">
                                {SITE.email}
                            </a>
                        </li>
                    </ul>
                </div>

                <div>
                    <h4 className="ll-eyebrow text-brand-mint mb-5">Horaires</h4>
                    <ul className="space-y-1.5 text-sm text-brand-cream/85 font-sans">
                        {SITE.hours.map((h) => (
                            <li key={h.day} className="flex justify-between gap-4">
                                <span>{h.day}</span>
                                <span className="text-brand-cream/65">{h.time}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h4 className="ll-eyebrow text-brand-mint mb-5">Liens</h4>
                    <ul className="space-y-2 text-brand-cream/85 font-sans">
                        <li><a href="/#a-propos" className="hover:text-brand-mint">La pharmacie</a></li>
                        <li><a href="/#equipe" className="hover:text-brand-mint">Équipe</a></li>
                        <li><a href="/#services" className="hover:text-brand-mint">Services</a></li>
                        <li><a href="/rendez-vous" className="hover:text-brand-mint">Rendez-vous</a></li>
                        <li><a href="/#contact" className="hover:text-brand-mint">Contact</a></li>
                    </ul>
                    <div className="mt-6 flex items-center gap-3">
                        <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram-link" className="h-10 w-10 rounded-full border border-brand-cream/25 inline-flex items-center justify-center hover:bg-brand-cream hover:text-brand-ink transition-colors">
                            <Instagram className="h-4 w-4" />
                        </a>
                    </div>
                </div>
            </div>

            <div className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-12 lg:pb-16 text-center">
                <a
                    href={LEGAL.studioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Site créé par Studio by G"
                    className="ll-studio-mark group inline-block font-['Syne',sans-serif] font-extrabold uppercase leading-none tracking-[0.02em] text-[2.6rem] sm:text-7xl lg:text-8xl"
                    data-testid="footer-studio-wordmark"
                >
                    <span className="ll-studio-outline">Studio by</span>{" "}
                    <span className="text-brand-mint transition-colors duration-300 group-hover:text-brand-cream">G</span>
                </a>
            </div>

            <div className="border-t border-brand-cream/15">
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs font-sans text-brand-cream/55">
                    <p>© {new Date().getFullYear()} Pharmacie La Lutrive — Tous droits réservés.</p>
                    <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-legal-links">
                        <Link to="/mentions-legales" className="hover:text-brand-cream transition-colors" data-testid="footer-legal-notice-link">Mentions légales</Link>
                        <Link to="/confidentialite" className="hover:text-brand-cream transition-colors" data-testid="footer-privacy-link">Confidentialité</Link>
                        <button type="button" onClick={openCookieSettings} className="hover:text-brand-cream transition-colors" data-testid="footer-cookie-settings-btn">Cookies</button>
                    </nav>
                    <p>
                        Site créé par{" "}
                        <a href={LEGAL.studioUrl} target="_blank" rel="noopener noreferrer" className="font-['Syne',sans-serif] font-bold tracking-[-0.025em] text-brand-cream hover:opacity-80 transition-opacity" data-testid="footer-studio-link">
                            Studio by <span className="text-[#00F0FF]">G</span>
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
